<script setup>
// Configuração da IA por caixa de entrada (ver AI.md).
import { ref, computed, watch, onMounted } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Button from 'dashboard/components-next/button/Button.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import TextArea from 'dashboard/components-next/textarea/TextArea.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import Select from 'dashboard/components-next/select/Select.vue';
import Spinner from 'dashboard/components-next/spinner/Spinner.vue';
import AiPageLayout from '../components/AiPageLayout.vue';
import aiAPI from 'dashboard/api/ai';
import { useAiInboxSettings } from '../composables/useAiInboxSettings';

const OUT_OF_HOURS_TOKEN = '{{proxima_abertura}}';

const { t } = useI18n();
const {
  meta,
  isLoading,
  selectedInboxId,
  selectedSetting,
  inboxOptions,
  providerConfigured,
  fetchSettings,
  replaceSetting,
} = useAiInboxSettings();

const form = ref({});
const isSaving = ref(false);

const defaults = computed(() => meta.value.defaults || {});
const maxTurnsValid = computed(() => {
  const value = Number(form.value.max_turns);
  return Number.isInteger(value) && value >= 1 && value <= 50;
});

const resetForm = setting => {
  if (!setting) return;
  form.value = {
    enabled: setting.enabled,
    assistant_name: setting.assistant_name || '',
    instructions: setting.instructions || '',
    handoff_message: setting.handoff_message || '',
    out_of_hours_message: setting.out_of_hours_message || '',
    waiting_message: setting.waiting_message || '',
    max_turns: String(setting.max_turns),
    handoff_team_id: setting.handoff_team_id || '',
  };
};

watch(selectedSetting, resetForm, { immediate: true });

const save = async () => {
  isSaving.value = true;
  try {
    const { data } = await aiAPI.updateInboxSetting(selectedInboxId.value, {
      ...form.value,
      max_turns: Number(form.value.max_turns),
      handoff_team_id: form.value.handoff_team_id || null,
    });
    replaceSetting(data);
    useAlert(t('AI.SETTINGS.SAVED'));
  } catch (error) {
    useAlert(error?.response?.data?.message || t('AI.SETTINGS.ERROR'));
  } finally {
    isSaving.value = false;
  }
};

onMounted(() => {
  fetchSettings();
});
</script>

<template>
  <AiPageLayout
    :title="t('AI.SETTINGS.TITLE')"
    :description="t('AI.SETTINGS.DESCRIPTION')"
    :provider-warning="!providerConfigured"
  >
    <div
      v-if="isLoading && !inboxOptions.length"
      class="flex justify-center py-16"
    >
      <Spinner />
    </div>

    <p v-else-if="!inboxOptions.length" class="text-body-para text-n-slate-11">
      {{ t('AI.SETTINGS.NO_INBOXES') }}
    </p>

    <form
      v-else-if="selectedSetting"
      class="flex flex-col gap-6 pb-10"
      data-testid="ai-settings-form"
      @submit.prevent="save"
    >
      <!-- PortVia: com uma conexão só (o normal), não há o que escolher -->
      <div v-if="inboxOptions.length > 1" class="flex flex-col gap-2">
        <label class="text-label text-n-slate-12">{{
          t('AI.SETTINGS.INBOX')
        }}</label>
        <Select v-model="selectedInboxId" :options="inboxOptions" />
      </div>

      <div
        class="flex items-start justify-between gap-4 px-5 py-4 rounded-xl bg-n-solid-1 outline outline-1 outline-n-weak"
      >
        <div class="flex flex-col gap-1">
          <span class="text-heading-3 text-n-slate-12">{{
            t('AI.SETTINGS.ENABLED')
          }}</span>
          <span class="text-body-para text-n-slate-11">{{
            t('AI.SETTINGS.ENABLED_HELP')
          }}</span>
        </div>
        <Switch v-model="form.enabled" data-testid="ai-enabled-switch" />
      </div>

      <Input
        v-model="form.assistant_name"
        :label="t('AI.SETTINGS.ASSISTANT_NAME')"
        :placeholder="t('AI.SETTINGS.ASSISTANT_NAME_PLACEHOLDER')"
      />

      <TextArea
        v-model="form.instructions"
        :label="t('AI.SETTINGS.INSTRUCTIONS')"
        :placeholder="t('AI.SETTINGS.INSTRUCTIONS_PLACEHOLDER')"
        :message="t('AI.SETTINGS.INSTRUCTIONS_HELP')"
        :max-length="4000"
        min-height="8rem"
        max-height="20rem"
        auto-height
        show-character-count
      />

      <TextArea
        v-model="form.handoff_message"
        :label="t('AI.SETTINGS.HANDOFF_MESSAGE')"
        :placeholder="defaults.handoff_message"
        :message="t('AI.SETTINGS.HANDOFF_MESSAGE_HELP')"
        :max-length="1000"
        auto-height
      />

      <div class="flex flex-col gap-2">
        <TextArea
          v-model="form.out_of_hours_message"
          :label="t('AI.SETTINGS.OUT_OF_HOURS_MESSAGE')"
          :placeholder="defaults.out_of_hours_message"
          :message="
            t('AI.SETTINGS.OUT_OF_HOURS_MESSAGE_HELP', {
              token: OUT_OF_HOURS_TOKEN,
            })
          "
          :max-length="1000"
          auto-height
        />
        <p
          v-if="!selectedSetting.working_hours_enabled"
          class="text-label-small text-n-amber-11"
        >
          {{ t('AI.SETTINGS.WORKING_HOURS_DISABLED') }}
        </p>
      </div>

      <TextArea
        v-model="form.waiting_message"
        :label="t('AI.SETTINGS.WAITING_MESSAGE')"
        :placeholder="defaults.waiting_message"
        :message="t('AI.SETTINGS.WAITING_MESSAGE_HELP')"
        :max-length="1000"
        auto-height
        data-testid="ai-waiting-message"
      />

      <!-- PortVia: limite de respostas e time da transferência ficam fora da tela (design);
           o salvar mantém os valores gravados -->
      <div class="flex justify-end">
        <Button
          type="submit"
          :label="t('AI.SETTINGS.SAVE')"
          icon="i-lucide-check"
          :is-loading="isSaving"
          :disabled="!maxTurnsValid || !form.assistant_name"
        />
      </div>
    </form>
  </AiPageLayout>
</template>
